import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { workoutPlannerAPI } from '../../services/api'
import { EXERCISE_TYPE_CONFIG } from './workoutHelpers'
import { contentLanguage } from '../../i18n/supported'

export function ExercisePickerModal({ program: _program, onSave, onClose, isLoading }) {
  const { t, i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)
  const isGerman = currentLanguage === 'de'
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedExercise, setSelectedExercise] = useState(null)
  const [translations, setTranslations] = useState({})
  const [actualDate, setActualDate] = useState(new Date().toISOString().split('T')[0])

  const { data: exercisesData, isLoading: exercisesLoading } = useQuery({
    queryKey: ['exercises-picker', searchTerm],
    queryFn: () => workoutPlannerAPI.getExercises({
      search_term: searchTerm || undefined,
      limit: 50
    }),
    keepPreviousData: true
  })

  const exercises = exercisesData?.data?.exercises || []

  useEffect(() => {
    if (currentLanguage === 'en' || exercises.length === 0) return

    const fetchTranslations = async () => {
      const newTranslations = {}
      await Promise.allSettled(
        exercises.map(async (exercise) => {
          const cacheKey = `${exercise.id}-${currentLanguage}`
          if (translations[cacheKey]) return

          try {
            const response = await workoutPlannerAPI.translateExercise(exercise.id, currentLanguage)
            newTranslations[cacheKey] = response.data
          } catch {
            // Fallback to English on error
          }
        })
      )
      if (Object.keys(newTranslations).length > 0) {
        setTranslations(prev => ({ ...prev, ...newTranslations }))
      }
    }

    fetchTranslations()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- translations state would cause infinite loop
  }, [exercises, currentLanguage])

  const handleConfirm = () => {
    if (!selectedExercise) return
    onSave({
      exercise_id: selectedExercise.id,
      actual_date: actualDate
    })
  }

  const getExerciseType = (exercise) => {
    return (exercise.exercise_type || 'strength').toLowerCase()
  }

  const getExerciseName = (exercise) => {
    if (currentLanguage === 'en') return exercise.name_en
    const cacheKey = `${exercise.id}-${currentLanguage}`
    return translations[cacheKey]?.name || exercise.name_en
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{t('programs.addExercise')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input w-full pl-10"
              placeholder={isGerman ? 'Übung suchen...' : 'Search exercises...'}
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {exercisesLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : exercises.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm
                ? (isGerman ? 'Keine Übungen gefunden' : 'No exercises found')
                : (isGerman ? 'Beginne zu tippen um zu suchen' : 'Start typing to search')}
            </div>
          ) : (
            <div className="grid gap-2">
              {exercises.map((exercise) => {
                const exerciseType = getExerciseType(exercise)
                const config = EXERCISE_TYPE_CONFIG[exerciseType] || EXERCISE_TYPE_CONFIG.other
                const isSelected = selectedExercise?.id === exercise.id

                return (
                  <button
                    key={exercise.id}
                    onClick={() => setSelectedExercise(exercise)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-xl mr-3">{config.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {getExerciseName(exercise)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {t(`exercises.muscleGroups.${exercise.muscle_group}`, { defaultValue: exercise.muscle_group })}
                          {' • '}
                          {t(`exercises.exerciseTypes.${exerciseType}`, { defaultValue: exerciseType })}
                        </p>
                      </div>
                      <div
                        className="w-3 h-3 rounded-full ml-2"
                        style={{ backgroundColor: config.color }}
                      />
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {selectedExercise && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700">
                  {isGerman ? 'Datum:' : 'Date:'}
                </label>
                <input
                  type="date"
                  value={actualDate}
                  onChange={(e) => setActualDate(e.target.value)}
                  className="input py-1 px-2 text-sm"
                />
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-outline btn-sm"
                  disabled={isLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleConfirm}
                  className="btn-primary btn-sm"
                  disabled={isLoading}
                >
                  {isLoading ? t('common.saving') : t('programs.addExercise')}
                </button>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {isGerman ? 'Ausgewählt:' : 'Selected:'} <strong>{getExerciseName(selectedExercise)}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
